import { useState } from "react";
import { usePageMeta } from "@/lib/seo";
import { CheckCircle2, FileText, ShieldCheck, Users2 } from "lucide-react";
import { apiClient } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

const PLATFORM_OPTIONS = ["Instagram", "YouTube", "TikTok", "Facebook", "Twitter / X", "Blog or Website", "Email Newsletter", "Other"];
const AUDIENCE_OPTIONS = ["Under 1,000", "1,000 - 10,000", "10,000 - 50,000", "50,000 - 100,000", "100,000+"];

const emptyForm = {
  full_name: "",
  email: "",
  phone: "",
  website: "",
  primary_platform: "",
  audience_size: "",
  promotion_plan: "",
  reason: "",
  certified: false,
  hp_check: "",
};

const STEPS = [
  { icon: FileText, title: "Apply", desc: "Complete the affiliate application." },
  { icon: ShieldCheck, title: "Get Approved", desc: "Our team will review your application and online presence." },
  { icon: Users2, title: "Start Referring", desc: "Approved affiliates will receive onboarding instructions, referral tracking information, and commission details." },
];

export default function AffiliateProgram() {
  usePageMeta("Affiliate Program", "Partner with True Impact Peptides. Apply to our affiliate program for research-focused audiences.", "/affiliate-program");
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!form.certified) {
      toast.error("Please check the certification box to continue.");
      return;
    }
    setLoading(true);
    try {
      await apiClient.post("/affiliate-application", form);
      setSubmitted(true);
      setForm(emptyForm);
    } catch (err) {
      if (err?.response?.status === 429) {
        toast.error("Too many requests. Please try again later.");
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="affiliate-program-page">
      {/* Hero */}
      <div className="max-w-4xl mx-auto px-6 md:px-12 pt-16 pb-10 text-center">
        <span className="text-xs tracking-[0.2em] uppercase font-semibold text-brandblue-600">Affiliate Program</span>
        <h1 className="mt-3 text-4xl sm:text-5xl tracking-tighter font-medium text-slate-900 font-display">Become a True Impact Peptides Affiliate</h1>
        <p className="mt-5 text-slate-600 leading-relaxed max-w-2xl mx-auto">
          Partner with True Impact Peptides and earn commissions by referring qualified customers to our website.
          Complete the short application below, and our team will review your information.
        </p>
      </div>

      {/* Application form */}
      <div className="max-w-2xl mx-auto px-6 md:px-12 pb-16">
        {submitted ? (
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-10 text-center" data-testid="affiliate-success-message">
            <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto mb-4" />
            <h2 className="text-xl font-display font-semibold text-slate-900">Application Received</h2>
            <p className="mt-3 text-slate-600 leading-relaxed">
              Thank you for applying to the True Impact Peptides Affiliate Program. Our team will review your
              application and contact you using the email address you provided.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="bg-white border border-slate-200 rounded-xl shadow-sm p-6 sm:p-8 space-y-5" data-testid="affiliate-application-form">
            {/* Honeypot field, hidden from real users */}
            <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", overflow: "hidden" }}>
              <Label htmlFor="hp_check">Leave blank</Label>
              <Input id="hp_check" name="hp_check" type="text" tabIndex={-1} autoComplete="off" value={form.hp_check} onChange={(e) => update("hp_check", e.target.value)} />
            </div>

            <div>
              <Label>Full Name</Label>
              <Input required maxLength={200} value={form.full_name} onChange={(e) => update("full_name", e.target.value)} data-testid="affiliate-fullname-input" />
            </div>
            <div>
              <Label>Email Address</Label>
              <Input type="email" required maxLength={200} value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="affiliate-email-input" />
            </div>
            <div>
              <Label>Phone Number</Label>
              <Input type="tel" required maxLength={40} value={form.phone} onChange={(e) => update("phone", e.target.value)} data-testid="affiliate-phone-input" />
            </div>
            <div>
              <Label>Website or Social Media Profile</Label>
              <Input required maxLength={300} placeholder="https://" value={form.website} onChange={(e) => update("website", e.target.value)} data-testid="affiliate-website-input" />
            </div>
            <div>
              <Label>Primary Platform</Label>
              <Select value={form.primary_platform} onValueChange={(v) => update("primary_platform", v)} required>
                <SelectTrigger data-testid="affiliate-platform-select"><SelectValue placeholder="Select a platform" /></SelectTrigger>
                <SelectContent>
                  {PLATFORM_OPTIONS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Estimated Audience Size</Label>
              <Select value={form.audience_size} onValueChange={(v) => update("audience_size", v)} required>
                <SelectTrigger data-testid="affiliate-audience-select"><SelectValue placeholder="Select a range" /></SelectTrigger>
                <SelectContent>
                  {AUDIENCE_OPTIONS.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>How do you plan to promote True Impact Peptides?</Label>
              <Textarea required rows={4} maxLength={3000} value={form.promotion_plan} onChange={(e) => update("promotion_plan", e.target.value)} data-testid="affiliate-promotion-plan-input" />
            </div>
            <div>
              <Label>Why would you like to become an affiliate?</Label>
              <Textarea required rows={4} maxLength={3000} value={form.reason} onChange={(e) => update("reason", e.target.value)} data-testid="affiliate-reason-input" />
            </div>

            <div className="flex items-start gap-3 pt-2">
              <Checkbox
                id="affiliate-certify"
                checked={form.certified}
                onCheckedChange={(v) => update("certified", !!v)}
                required
                data-testid="affiliate-certify-checkbox"
                className="mt-0.5"
              />
              <Label htmlFor="affiliate-certify" className="text-sm text-slate-600 leading-relaxed font-normal">
                I certify that I am 21 years of age or older and agree to promote True Impact Peptides responsibly.
                I understand that all products sold by True Impact Peptides are intended strictly for laboratory
                research purposes only and are not for human consumption. I agree not to make any medical,
                therapeutic, or disease treatment claims when promoting these products.
              </Label>
            </div>

            <Button type="submit" disabled={loading || !form.certified} className="w-full rounded-full btn-lift" data-testid="affiliate-submit-btn">
              {loading ? "Submitting..." : "Submit Affiliate Application"}
            </Button>
          </form>
        )}
      </div>

      {/* How It Works */}
      <div className="bg-[#F7FAFF] border-t border-slate-100 py-16">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <h2 className="text-2xl font-display font-semibold text-slate-900 text-center">How It Works</h2>
          <div className="mt-10 grid sm:grid-cols-3 gap-8">
            {STEPS.map((s, i) => (
              <div key={s.title} className="text-center" data-testid={`affiliate-step-${i + 1}`}>
                <div className="h-12 w-12 rounded-full bg-gradient-to-r from-blue-900 to-blue-500 flex items-center justify-center mx-auto shadow-blue-glow-sm">
                  <s.icon className="h-5 w-5 text-white" strokeWidth={1.5} />
                </div>
                <p className="mt-4 text-xs font-semibold text-brandblue-600 uppercase tracking-wider">Step {i + 1}</p>
                <h3 className="mt-1 text-lg font-display font-semibold text-slate-900">{s.title}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
          <p className="mt-10 text-center text-sm text-slate-500 italic">
            Commission rates and affiliate program details will be provided upon approval.
          </p>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="max-w-3xl mx-auto px-6 md:px-12 py-10 text-center">
        <p className="text-xs text-slate-400 leading-relaxed" data-testid="affiliate-disclaimer">
          Affiliate approval is at the sole discretion of True Impact Peptides. Affiliates may not make medical
          claims, therapeutic claims, or represent any product as approved for human use. All products sold by
          True Impact Peptides are intended strictly for laboratory research purposes only.
        </p>
      </div>
    </div>
  );
}
